import React, { useState } from 'react';
import { editIcon } from '../../utils/images';
import { TagResponse } from '../../types/Product';
import './TagCard.scss';

interface TagCardProps {
  tag: TagResponse;
  onUpdate?: (id: number, name: string) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;
}

export const TagCard: React.FC<TagCardProps> = ({ 
  tag, 
  onUpdate,
  onDelete
}) => {
  const [name, setName] = useState(tag.name);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!onUpdate || name.trim() === '' || name === tag.name) {
      setIsEditing(false);
      setName(tag.name);
      return;
    }

    try {
      setIsUpdating(true);
      await onUpdate(tag.id, name.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating tag:', error);
      setName(tag.name);
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setName(tag.name);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    const productCountText = tag.product_count 
      ? ` This will also remove the tag from ${tag.product_count} product${tag.product_count !== 1 ? 's' : ''}.`
      : '';
    
    if (window.confirm(`Are you sure you want to delete the tag "${tag.name}"?${productCountText}`)) {
      try {
        setIsUpdating(true);
        await onDelete(tag.id);
      } catch (error) {
        console.error('Error deleting tag:', error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="tagCard">
      <div className="tagCard__content">
        <div className="tagCard__header">
          <div className="tagCard__title">
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleSave}
                onKeyPress={handleKeyPress}
                className="tagCard__input"
                autoFocus
                disabled={isUpdating}
              />
            ) : (
              <span className="tagCard__name">{tag.name}</span>
            )}
          </div>
          <div className="tagCard__actions">
            <button
              onClick={handleEditClick}
              className="tagCard__edit"
              disabled={isEditing || isUpdating}
            >
              <img src={editIcon} alt="Edit" />
            </button>
            <button
              onClick={handleDelete}
              className="tagCard__delete"
              disabled={isUpdating}
            >
              Delete
            </button>
          </div>
        </div>
        <div className="tagCard__info">
          <span className="tagCard__id">ID: {tag.id}</span>
          {tag.product_count !== undefined && (
            <span className="tagCard__productCount">
              {tag.product_count} product{tag.product_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      {isUpdating && (
        <div className="tagCard__loading">
          <span>Updating...</span>
        </div>
      )}
    </div>
  );
};